import type { OrganizationDetails } from "~vue/appTypes";
import BasePopover from "~vue/components/BasePopover.vue";
import escapeRegExp from "lodash/escapeRegExp";
import uniqBy from "lodash/uniqBy";
import type { Hooks } from "marked";
import { computed, type ComputedRef, h, inject, nextTick, type Ref, render, watch } from "vue";

import type { ChatAction, PluckedAction } from "./chatActions";
import ChatMyPeopleAccordion from "./ChatDetail/ChatMyPeopleAccordion.vue";
import { useActionHandler } from "./composables/useActionHandler";
import type { ConnectionProp } from "./types/connection";

export function useConnectionPopovers(hooks: Ref<Hooks>, componentId: string, connectionChatMessageActions: ComputedRef<PluckedAction<ChatAction.TAGGED_PERSON>[]>) {
    const sendMessage = inject<(content: string, options?: Record<string, unknown>) => void>("sendMessage", () => {});
    const connections = inject<ComputedRef<ConnectionProp[]>>(
        "connections",
        computed(() => []),
    );
    const coachingSessionUuid = inject<string>("coachingSessionUuid", "");
    const publicProfileEnabled = inject<boolean>("publicProfileEnabled", false);
    // Injected here and forwarded as props below: the card is mounted with a bare `render()`, which starts
    // a fresh provides chain, so anything it tries to inject itself resolves to the default.
    const organizationDetails = inject<ComputedRef<OrganizationDetails | null>>(
        "organization_details",
        computed(() => ({})),
    );
    const collaborationProfileInviteUrl = inject<string>("collaborationProfileInviteUrl", "");
    const showChatToast = inject<(message: string) => void>("showChatToast", () => {});
    const { execute } = useActionHandler();

    const renderConnectionPopover = async () => {
        await nextTick(() => {
            if (connectionChatMessageActions.value.length === 0) {
                return;
            }
            const connectionNodes = document.querySelectorAll(`[data-message-component-id="${componentId}"] [data-connection-id]`);
            connectionNodes.forEach((node) => {
                if (node instanceof HTMLElement === false) {
                    return;
                }
                const connection = connections.value.find((conn) => conn.id === node.dataset.connectionId);
                if (!connection) {
                    return;
                }

                const actionEntry = connectionChatMessageActions.value.find((action) => action.line.action_params.connection === connection.id);
                const matchText = actionEntry?.line.action_params.match_text;

                if (connection.declined_at || actionEntry?.line.action_state?.dismissed) {
                    render(h("span", matchText ?? connection.name), node);
                    return;
                }

                render(
                    h(
                        BasePopover,
                        {
                            sideOffset: 10,
                            collisionPadding: 16,
                        },
                        {
                            trigger: () =>
                                h(
                                    "span",
                                    {
                                        class: "underline decoration-1 underline-offset-2 text-valence-primary-500 cursor-pointer",
                                    },
                                    matchText ?? connection.name,
                                ),
                            content: () => {
                                if (!actionEntry) {
                                    return;
                                }
                                return h(ChatMyPeopleAccordion, {
                                    class: "w-80",
                                    connectionChatMessageAction: actionEntry,
                                    execute,
                                    sendMessage,
                                    coachingSessionUuid,
                                    connections,
                                    publicProfileEnabled,
                                    organizationDetails,
                                    collaborationProfileInviteUrl,
                                    showToast: showChatToast,
                                });
                            },
                        },
                    ),
                    node,
                );
            });
        });
    };

    watch(
        connectionChatMessageActions,
        async () => {
            if (connectionChatMessageActions.value.length > 0) {
                const connectionRegexes = uniqBy(connectionChatMessageActions.value, "line.action_params.connection")
                    .filter((action) => {
                        const connection = connections.value.find((conn) => conn.id === action.line.action_params.connection);
                        return connection && !connection.declined_at;
                    })
                    .map((action) => {
                        return {
                            regex: new RegExp(`\\b${escapeRegExp(action.line.action_params.match_text)}\\b`),
                            connectionId: action.line.action_params.connection,
                        };
                    });

                hooks.value.postprocess = (tokens) => {
                    let newTokens = tokens;
                    connectionRegexes.forEach(({ regex, connectionId }) => {
                        newTokens = newTokens.replace(regex, `<span data-connection-id="${connectionId}"></span>`);
                    });
                    return newTokens;
                };
                await renderConnectionPopover();
            }
        },
        { immediate: true },
    );

    watch(
        connections,
        async () => {
            await renderConnectionPopover();
        },
        { immediate: true, deep: true },
    );
}
